import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const plans = [
  {
    name: "Free",
    price: "$0",
    period: "",
    description: "Try it out with a single lookup",
    features: ["1 Listing Audit", "AI Image Check", "Basic Scam Alert", "Direct-Link Dashboard"],
    highlighted: false,
  },
  {
    name: "Standard",
    price: "$9.99",
    period: "",
    description: "Perfect for your next trip",
    features: ["Up to 4 Audits", "Direct-Link Dashboard", "Cross-Platform Search"],
    highlighted: false,
  },
  {
    name: "The Planner",
    price: "$19.99",
    period: "",
    description: "Best for frequent travelers",
    badge: "Best Value",
    features: ["Up to 10 Audits", "Manual Owner Check", "Direct-Link Dashboard"],
    highlighted: true,
  },
  {
    name: "Nomad VIP",
    price: "$99",
    period: "/mo",
    description: "For digital nomads & power users",
    features: ["Up to 50 Audits", "Direct Host Negotiation", "Manual Owner Check", "Priority Manual Verification & 1-on-1 Support"],
    highlighted: false,
  },
];

const Pricing = () => {
  return (
    <section id="pricing" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/4 w-72 h-72 bg-accent/40 rounded-full blur-3xl -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-accent text-accent-foreground text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Pricing
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-3">
            Simple, transparent pricing
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Choose the plan that fits your travel style. No hidden fees, cancel anytime.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className={plan.highlighted ? "relative" : ""}
            >
              {plan.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-gradient-hero text-primary-foreground text-xs font-bold px-4 py-1 rounded-full shadow-savings flex items-center gap-1.5">
                    <Star className="w-3 h-3" />
                    {plan.badge}
                  </span>
                </div>
              )}
              <Card
                className={`h-full flex flex-col transition-shadow ${
                  plan.highlighted
                    ? "border-2 border-primary shadow-savings ring-1 ring-primary/20"
                    : "hover:shadow-card-hover"
                }`}
              >
                <CardHeader className="pb-4">
                  <CardDescription className="text-xs font-semibold uppercase tracking-wider">
                    {plan.name}
                  </CardDescription>
                  <CardTitle className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold tracking-tight">{plan.price}</span>
                    {plan.period && (
                      <span className="text-base font-normal text-muted-foreground">{plan.period}</span>
                    )}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                </CardHeader>

                <CardContent className="flex-1">
                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2.5 text-sm">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${plan.highlighted ? 'bg-primary/15 text-primary' : 'bg-accent text-accent-foreground'}`}>
                          <Check className="w-3 h-3" />
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter>
                  <Button
                    className={`w-full rounded-xl ${
                      plan.highlighted
                        ? "bg-gradient-hero text-primary-foreground shadow-savings hover:opacity-90"
                        : ""
                    }`}
                    variant={plan.highlighted ? "default" : "outline"}
                  >
                    Get Started
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
